import React from 'react'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { useFamilyAxes } from '../../hooks/useFamilyAxes'
import { LEGACY_AXIS } from '../../utils/familyAxes'
import { hiddenValueClass } from '../../utils/hiddenClass'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import style from './Subproducts.module.scss'

interface ISubproductsProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  onItemSelect: (index: number) => void
}

// AYTM-12710: multi-axis selector for Chewy. Filters incompatible on lower axes — same
// pattern as Walmart/Target/Standart. Spec §Multi-Axis Selector Pattern §Per-retailer divergence.
const Subproducts = ({ schema, answer, onItemSelect }: ISubproductsProps) => {
  const viewport = useViewport()
  const { languageTag } = useSurveyLocale()
  const { childrenProducts, axes, currentSelections, findSKUForValue, isCompatibleWithUpperAxes, isHiddenInPreview } =
    useFamilyAxes({
      answer,
      schema
    })

  if (childrenProducts.length <= 1 || axes.length === 0) return null

  function selectValue(axisName: string, value: string) {
    const targetSKU = findSKUForValue(axisName, value)
    if (!targetSKU) return
    onItemSelect(targetSKU.index)
  }

  return (
    <div className={`${style.subproducts} ${viewport.mobile ? style.mobile : ''}`}>
      {axes.map((axis, axisIndex) => {
        const selectedValue = currentSelections[axis.name] || ''
        return (
          <React.Fragment key={axis.name}>
            {axis.name !== LEGACY_AXIS && (
              <p className={style.title}>
                <span className={style.titleName}>{axis.name}:</span>
                <span className={style.titleValue}>{selectedValue}</span>
              </p>
            )}
            <div className={style.items}>
              {axis.values.map(value => {
                // Chewy hides incompatible values entirely on lower axes; axis 1 always renders every value,
                // and a preview-hidden value renders regardless, washed out. Spec §Multi-Axis Selector Pattern.
                const hiddenInPreview = isHiddenInPreview(axis.name, value)
                if (!hiddenInPreview && !isCompatibleWithUpperAxes(axisIndex, value)) return null

                const targetSKU = findSKUForValue(axis.name, value)
                const isActive = selectedValue === value
                const regularPrice = targetSKU?.price
                const discountedPrice = targetSKU?.discounted_price
                // discounted_price is 0 (not null) for non-discounted SKUs — a bare null-check made every tile render $0
                const hasDiscount = Boolean(discountedPrice) && Boolean(regularPrice) && discountedPrice < regularPrice
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => selectValue(axis.name, value)}
                    className={`${style.item} ${isActive ? style.itemActive : ''} ${hiddenValueClass(hiddenInPreview)}`}
                  >
                    <span className={style.itemValue}>{value}</span>
                    {viewport.mobile && targetSKU && (
                      <span
                        className={style.itemPrice}
                        dangerouslySetInnerHTML={{
                          __html: formatPriceWithLocale(
                            hasDiscount ? discountedPrice! : regularPrice!,
                            schema.price_mask,
                            languageTag,
                            true,
                            false
                          )
                        }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default Subproducts
